import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { 
  Search, 
  Filter,
  ChevronLeft,
  ChevronRight,
  User,
  MessageSquare,
  Calendar,
  X,
  Plus,
  Download,
  Upload,
  Trash2,
  RotateCcw,
  Star,
  TrendingUp,
  Zap,
  Target,
  Award,
  Activity,
  CheckSquare,
  Square
} from "lucide-react";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { safeLocalStorage } from "@/utils/safeStorage";
import { useLanguage } from "@/contexts/LanguageContext";
import { FileSpreadsheet, FileText } from "lucide-react";
import { TELEGRAM_CATEGORIES, getCategoryColor } from "@/constants/telegramCategories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom hook for debouncing search
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
}

// Activity level config
const ACTIVITY_LEVELS = {
  very_active: { label: 'نشط جداً', labelEn: 'Very Active', color: 'bg-green-500', icon: Zap },
  active: { label: 'نشط', labelEn: 'Active', color: 'bg-blue-500', icon: TrendingUp },
  moderate: { label: 'متوسط', labelEn: 'Moderate', color: 'bg-yellow-500', icon: Activity },
  low: { label: 'منخفض', labelEn: 'Low', color: 'bg-gray-500', icon: User }
};

// Quality config
const QUALITY_LEVELS = {
  excellent: { label: 'ممتاز', labelEn: 'Excellent', color: 'bg-gradient-to-r from-yellow-400 to-orange-500', icon: Award },
  good: { label: 'جيد', labelEn: 'Good', color: 'bg-gradient-to-r from-green-400 to-emerald-500', icon: Star },
  average: { label: 'متوسط', labelEn: 'Average', color: 'bg-gradient-to-r from-blue-400 to-cyan-500', icon: Target },
  low: { label: 'منخفض', labelEn: 'Low', color: 'bg-gradient-to-r from-gray-400 to-gray-500', icon: User }
};

// Interest colors - now using Telegram categories
const INTEREST_COLORS = {};
TELEGRAM_CATEGORIES.forEach(cat => {
  INTEREST_COLORS[cat.id] = cat.color;
});

// Legacy interest colors for backward compatibility
const LEGACY_INTEREST_COLORS = {
  crypto: '#98D8C8',
  tech: '#96CEB4',
  marketing: '#BB8FCE',
  trading: '#00FA9A',
  ecommerce: '#FF69B4',
  education: '#FFD700',
  news: '#FF6B6B',
  // Arabic short names
  'تجارة': '#DDA0DD',
  'تقنية': '#96CEB4',
  'تسويق': '#BB8FCE',
  'صحة': '#00FF7F',
  'تقني': '#96CEB4',
  'أخبار': '#FF6B6B',
  'ترفيه': '#45B7D1',
  'اقتصاد': '#FFEAA7',
  'كريبتو': '#98D8C8',
  'سفر': '#F7DC6F',
  'تصميم': '#F8B500',
  'سياسة': '#00CED1',
  'فن': '#FF69B4',
  'قانون': '#32CD32',
  'تعليم': '#FFD700',
  'كتب': '#FF4500',
  'رياضة': '#ADFF2F',
  'موضة': '#FF6347',
  'طب': '#7B68EE',
  'أعمال': '#DDA0DD',
  'وظائف': '#00FA9A'
};

// Short Arabic to English mapping for translation
const ARABIC_TO_ENGLISH = {
  'تجارة': 'Commerce',
  'تقنية': 'Technology',
  'تسويق': 'Marketing',
  'صحة': 'Health',
  'تقني': 'Tech',
  'أخبار': 'News',
  'ترفيه': 'Entertainment',
  'اقتصاد': 'Economics',
  'كريبتو': 'Crypto',
  'سفر': 'Travel',
  'تصميم': 'Design',
  'سياسة': 'Politics',
  'فن': 'Art',
  'قانون': 'Law',
  'تعليم': 'Education',
  'كتب': 'Books',
  'رياضة': 'Sport',
  'موضة': 'Fashion',
  'طب': 'Medicine',
  'أعمال': 'Business',
  'وظائف': 'Career'
};

// Helper to translate interest name based on language
const translateInterest = (interest, isRTL) => {
  if (!interest) return interest;
  
  // If interface is Arabic, try to keep Arabic or use category Arabic name
  if (isRTL) {
    const category = TELEGRAM_CATEGORIES.find(c => 
      c.id === interest || 
      c.name_ar === interest || 
      c.name_en?.toLowerCase() === interest?.toLowerCase()
    );
    return category ? category.name_ar : interest;
  }
  
  // If interface is English, translate Arabic to English
  if (ARABIC_TO_ENGLISH[interest]) {
    return ARABIC_TO_ENGLISH[interest];
  }
  
  // Try to find in TELEGRAM_CATEGORIES
  const category = TELEGRAM_CATEGORIES.find(c => 
    c.id === interest || 
    c.name_ar === interest || 
    c.name_en?.toLowerCase() === interest?.toLowerCase()
  );
  
  return category ? category.name_en : interest;
};

// Helper to handle API errors
const handleApiError = (error, navigate, t) => {
  if (error.response?.status === 401) {
    toast.error(t('errors.sessionExpired') || 'انتهت الجلسة - يرجى تسجيل الدخول');
    safeLocalStorage.removeItem('auth_token');
    safeLocalStorage.removeItem('user');
    navigate('/login');
  } else {
    toast.error(t('errors.serverError'));
  }
};

// Circular Progress Component for filter stats
const CircularProgress = ({ percentage, size = 20, color = '#00F0FF' }) => {
  const radius = (size - 4) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  // Color based on percentage
  const getColor = () => {
    if (percentage >= 40) return '#22C55E'; // Green
    if (percentage >= 20) return '#F59E0B'; // Yellow/Orange
    return '#3B82F6'; // Blue
  };
  
  const fillColor = color === 'auto' ? getColor() : color;
  
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.1)"
        strokeWidth="3"
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={fillColor}
        strokeWidth="3"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
    </svg>
  );
};

const MemberRow = ({ member, onSelect, onToggleSelect, isSelected, t, isRTL }) => {
  // Get activity level - ensure it's read correctly
  const activityLevel = member.activity_level || 'low';
  const activityConfig = ACTIVITY_LEVELS[activityLevel];
  
  // Get quality - ensure it's read correctly from nested object
  const qualityScore = member.quality?.score || 0;
  const qualityLabel = member.quality?.label || 'low';
  const qualityConfig = QUALITY_LEVELS[qualityLabel];
  
  // Fallback if config not found
  const finalActivityConfig = activityConfig || ACTIVITY_LEVELS.low;
  const finalQualityConfig = qualityConfig || QUALITY_LEVELS.low;
  
  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onToggleSelect(member.id);
  };
  
  // Format member name professionally
  const formatMemberName = () => {
    const firstName = member.first_name || '';
    const lastName = member.last_name || '';
    let fullName = `${firstName} ${lastName}`.trim();
    
    // Fallback to other name fields
    if (!fullName) {
      fullName = member.name || member.display_name || member.username || '';
    }
    
    // Truncate if too long (max 25 chars)
    if (fullName.length > 25) {
      return fullName.substring(0, 22) + '...';
    }
    return fullName || (isRTL ? 'بدون اسم' : 'Unknown');
  };
  
  // Format username
  const formatUsername = () => {
    if (member.username) {
      const username = member.username.startsWith('@') ? member.username : `@${member.username}`;
      // Truncate if too long
      if (username.length > 18) {
        return username.substring(0, 15) + '...';
      }
      return username;
    }
    return isRTL ? 'بدون معرّف' : 'No username';
  };
  
  return (
    <tr 
      data-testid={`member-row-${member.id}`}
      className={`table-row cursor-pointer hover:bg-white/5 transition-colors ${isSelected ? 'bg-cyan-500/10' : ''}`}
      onClick={() => onSelect(member)}
    >
      <td style={{ width: '50px', textAlign: 'center', padding: '0.75rem 0.5rem' }}>
        <div 
          className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer mx-auto transition-all ${
            isSelected 
              ? 'bg-cyan-500 border-cyan-500' 
              : 'border-neutral-500 hover:border-cyan-400'
          }`}
          onClick={handleCheckboxClick}
          data-testid={`select-member-${member.id}`}
        >
          {isSelected && (
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      </td>
      <td style={{ width: '20%', padding: '0.75rem 1rem' }}>
        <div className="flex items-center gap-3" style={{ justifyContent: isRTL ? 'flex-start' : 'flex-start' }}>
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500/30 to-blue-500/30 flex items-center justify-center flex-shrink-0 border border-cyan-500/20">
            <User size={18} className="text-cyan-400" />
          </div>
          {/* Name & Username */}
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-white text-sm truncate" title={`${member.first_name || ''} ${member.last_name || ''}`.trim()}>
              {formatMemberName()}
            </p>
            <p className={`text-xs truncate ${member.username ? 'text-cyan-400' : 'text-neutral-500 italic'}`} title={member.username ? `@${member.username}` : ''}>
              {formatUsername()}
            </p>
          </div>
        </div>
      </td>
      <td style={{ width: '12%', textAlign: 'center', padding: '0.75rem 1rem' }}>
        <div className="flex items-center justify-center gap-2">
          <span className={`w-2 h-2 rounded-full ${finalActivityConfig.color}`}></span>
          <span className="text-sm">
            {isRTL ? finalActivityConfig.label : finalActivityConfig.labelEn}
          </span>
        </div>
      </td>
      <td style={{ width: '12%', textAlign: 'center', padding: '0.75rem 1rem' }}>
        <div className="flex justify-center">
          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${finalQualityConfig.color} text-white`}>
            <span>{qualityScore}</span>
            <span className="opacity-75">/ 100</span>
          </div>
        </div>
      </td>
      <td style={{ width: '20%', textAlign: 'center', padding: '0.75rem 1rem' }}>
        <div className="flex flex-wrap gap-1 justify-center">
          {member.interests?.slice(0, 2).map((interest, idx) => {
            const color = INTEREST_COLORS[interest] || LEGACY_INTEREST_COLORS[interest] || '#808080';
            const displayName = translateInterest(interest, isRTL);
            return (
              <span 
                key={idx} 
                className="px-2 py-0.5 rounded-full text-xs text-white"
                style={{ backgroundColor: color }}
              >
                {displayName}
              </span>
            );
          })}
          {member.interests?.length > 2 && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-neutral-600 text-white">
              +{member.interests.length - 2}
            </span>
          )}
          {(!member.interests || member.interests.length === 0) && (
            <span className="text-neutral-500 text-sm">-</span>
          )}
        </div>
      </td>
      <td style={{ width: '10%', textAlign: 'center', padding: '0.75rem 1rem' }} className="font-mono text-sm">
        {member.messages_count || member.activities_count || 0}
      </td>
      <td style={{ width: '14%', textAlign: 'center', padding: '0.75rem 1rem' }} className="text-neutral-500 text-sm">
        {member.created_at ? new Date(member.created_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US') : '-'}
      </td>
    </tr>
  );
};

const MemberDetail = ({ member, onClose, onDelete, t, isRTL, deleteLoading }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const activityConfig = ACTIVITY_LEVELS[member?.activity_level] || ACTIVITY_LEVELS.low;
  const qualityConfig = QUALITY_LEVELS[member?.quality?.label] || QUALITY_LEVELS.low;
  const QualityIcon = qualityConfig.icon;
  
  const handleDelete = async () => {
    if (onDelete && member) {
      await onDelete(member.id);
      setShowDeleteConfirm(false);
    }
  };
  
  // Helper function to get member display name
  const getMemberDisplayName = (m) => {
    if (m.first_name || m.last_name) {
      return `${m.first_name || ''} ${m.last_name || ''}`.trim();
    }
    if (m.name) return m.name;
    if (m.display_name) return m.display_name;
    if (m.username) return m.username;
    return isRTL ? 'بدون اسم' : 'Unknown';
  };

  return (
    <>
    <Dialog open={!!member} onOpenChange={() => onClose()}>
      <DialogContent 
        className="glass w-[420px] max-w-[95vw]"
        style={{
          border: '2px solid #EF4444',
          borderRadius: '16px',
          boxShadow: '0 0 30px rgba(239, 68, 68, 0.4)',
          padding: '24px',
          overflow: 'hidden'
        }}
      >
        {/* Title */}
        <div className="text-center mb-4">
          <h2 className="text-xl font-bold text-white">{isRTL ? 'تفاصيل العضو' : 'Member Details'}</h2>
        </div>
        
        {member && (
          <div className="space-y-4">
            {/* Avatar & Name Section */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center mb-3">
                <User size={32} className="text-black" />
              </div>
              <h3 className="text-lg font-bold text-white">
                {getMemberDisplayName(member)}
              </h3>
              <p className="text-cyan-400 text-sm">
                {member.username ? `@${member.username}` : (isRTL ? 'بدون معرّف' : 'No username')}
              </p>
              <p className="text-xs text-neutral-500 font-mono">ID: {member.telegram_id || member.id || '-'}</p>
            </div>

            {/* Activity & Quality Badges */}
            <div className="flex justify-center gap-2 flex-wrap">
              <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${activityConfig?.color || 'bg-gray-500'} text-white`}>
                {isRTL ? (activityConfig?.label || 'منخفض') : (activityConfig?.labelEn || 'Low')}
              </span>
              <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${qualityConfig?.color || 'bg-gray-500'} text-white`}>
                ({member.quality?.score || 0}/100) {isRTL ? (qualityConfig?.label || 'منخفض') : (qualityConfig?.labelEn || 'Low')}
              </span>
            </div>

            {/* Stats - 3 columns - FIXED ORDER */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-black/40 rounded-lg p-3">
                <p className="text-xl font-bold text-cyan-400">{member.messages_count || member.activities_count || 0}</p>
                <p className="text-xs text-neutral-400 mt-1">{isRTL ? 'الرسائل' : 'Messages'}</p>
              </div>
              <div className="bg-black/40 rounded-lg p-3">
                <p className="text-xl font-bold text-green-400">{member.groups?.length || 1}</p>
                <p className="text-xs text-neutral-400 mt-1">{isRTL ? 'المجموعات' : 'Groups'}</p>
              </div>
              <div className="bg-black/40 rounded-lg p-3">
                <p className="text-xl font-bold text-purple-400">{Array.isArray(member.interests) ? member.interests.length : 0}</p>
                <p className="text-xs text-neutral-400 mt-1">{isRTL ? 'الاهتمامات' : 'Interests'}</p>
              </div>
            </div>

            {/* Interests Tags */}
            <div>
              <p className="text-sm text-neutral-400 mb-2">{isRTL ? 'الاهتمامات' : 'Interests'}</p>
              <div className="flex flex-wrap gap-2">
                {Array.isArray(member.interests) && member.interests.length > 0 ? (
                  member.interests.slice(0, 5).map((interest, idx) => {
                    const color = INTEREST_COLORS[interest] || LEGACY_INTEREST_COLORS[interest] || '#808080';
                    const displayName = translateInterest(interest, isRTL);
                    return (
                      <span 
                        key={idx} 
                        className="px-3 py-1 rounded-full text-xs text-white"
                        style={{ backgroundColor: color }}
                      >
                        {displayName}
                      </span>
                    );
                  })
                ) : (
                  <span className="text-neutral-500 text-sm">{isRTL ? 'لم يتم التصنيف بعد' : 'Not classified yet'}</span>
                )}
              </div>
            </div>

            {/* Dates Row */}
            <div className="flex justify-between text-xs text-neutral-400 border-t border-white/10 pt-3">
              <span>{isRTL ? 'تاريخ الإضافة' : 'Date Added'}: {member.created_at ? new Date(member.created_at).toLocaleDateString() : '-'}</span>
              <span>{isRTL ? 'آخر ظهور' : 'Last Seen'}: {member.last_seen ? new Date(member.last_seen).toLocaleDateString() : '-'}</span>
            </div>
            
            {/* Delete Button */}
            <Button
              data-testid="delete-member-btn"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white font-bold py-3 rounded-lg"
              disabled={deleteLoading}
            >
              <Trash2 size={18} className="ml-2 inline" />
              {isRTL ? 'حذف العضو' : 'Delete Member'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
    
    {/* Delete Confirmation Dialog */}
    <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
      <AlertDialogContent className="glass border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-red-400">
            {isRTL ? 'تأكيد حذف العضو' : 'Confirm Delete Member'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isRTL 
              ? `هل أنت متأكد من حذف "${member?.first_name} ${member?.last_name || ''}"؟ لا يمكن التراجع عن هذا الإجراء.`
              : `Are you sure you want to delete "${member?.first_name} ${member?.last_name || ''}"? This action cannot be undone.`
            }
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleDelete}
            className="bg-red-500 hover:bg-red-600"
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <span className="spinner-sm ml-2"></span>
            ) : (
              <Trash2 size={16} className="ml-2" />
            )}
            {isRTL ? 'حذف' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default function Members() {
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteSelectedDialog, setShowDeleteSelectedDialog] = useState(false); // للحذف الجماعي
  const [canUndo, setCanUndo] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState(new Set()); // للأعضاء المحددين
  const [filterStats, setFilterStats] = useState(null); // إحصائيات الفلاتر
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  
  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedActivityLevel, setSelectedActivityLevel] = useState('');
  const [selectedQuality, setSelectedQuality] = useState('');
  const [selectedInterest, setSelectedInterest] = useState('');
  const [page, setPage] = useState(0);
  const limit = 20;
  
  // Debounce search input (300ms delay)
  const searchTerm = useDebounce(searchInput, 300);

  const fetchMembers = useCallback(async (search) => {
    try {
      setLoading(true);
      const token = safeLocalStorage.getItem('auth_token');
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('skip', page * limit);
      if (search) params.append('search', search);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedGroup) params.append('group_id', selectedGroup);
      if (selectedActivityLevel) params.append('activity_level', selectedActivityLevel);
      if (selectedQuality) params.append('quality', selectedQuality);
      if (selectedInterest) params.append('interest', selectedInterest);

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      console.log('Fetching members with params:', params.toString());
      
      const [membersRes, countRes] = await Promise.all([
        axios.get(`${API}/members?${params}`, { headers }),
        axios.get(`${API}/members/count?${params}`, { headers })
      ]);
      
      // Debug: Log first member data
      if (membersRes.data.length > 0) {
        console.log('First member from API:', membersRes.data[0]);
        console.log('activity_level:', membersRes.data[0].activity_level);
        console.log('quality:', membersRes.data[0].quality);
        console.log('messages_count:', membersRes.data[0].messages_count);
      }
      
      setMembers(membersRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      handleApiError(error, navigate, t);
    } finally {
      setLoading(false);
    }
  }, [page, selectedCategory, selectedGroup, selectedActivityLevel, selectedQuality, selectedInterest, navigate, t]);

  const fetchFilters = async () => {
    try {
      const token = safeLocalStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch categories and groups first (required)
      const [catRes, groupRes] = await Promise.all([
        axios.get(`${API}/categories`, { headers }),
        axios.get(`${API}/groups`, { headers })
      ]);
      
      console.log('Groups fetched:', groupRes.data);
      setCategories(catRes.data || []);
      setGroups(groupRes.data || []);
      
      // Fetch analytics separately (optional)
      try {
        const analyticsRes = await axios.get(`${API}/members/analytics`, { headers });
        setAnalytics(analyticsRes.data);
      } catch (analyticsError) {
        console.log('Analytics not available:', analyticsError.message);
        setAnalytics(null);
      }
    } catch (error) {
      console.error('Error fetching filters:', error);
      setGroups([]);
      setCategories([]);
    }
  };

  // Check if there are deleted members that can be restored
  const checkCanRestore = async () => {
    try {
      const token = safeLocalStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/members/can-restore`, { headers });
      setCanUndo(response.data.can_restore);
    } catch (error) {
      setCanUndo(false);
    }
  };

  useEffect(() => {
    fetchFilters();
    checkCanRestore();
  }, []);

  useEffect(() => {
    fetchMembers(searchTerm);
  }, [page, searchTerm, selectedCategory, selectedGroup, selectedActivityLevel, selectedQuality, selectedInterest, fetchMembers]);

  const totalPages = Math.ceil(totalCount / limit);

  const clearFilters = () => {
    setSearchInput('');
    setSelectedCategory('');
    setSelectedGroup('');
    setSelectedActivityLevel('');
    setSelectedQuality('');
    setSelectedInterest('');
    setPage(0);
  };

  // جلب إحصائيات الفلاتر
  const fetchFilterStats = useCallback(async () => {
    try {
      const token = safeLocalStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/members/filter-stats`, { headers });
      setFilterStats(res.data);
    } catch (error) {
      console.error('Failed to fetch filter stats:', error);
    }
  }, []);

  // جلب الإحصائيات عند تحميل الصفحة
  useEffect(() => {
    fetchFilterStats();
  }, [fetchFilterStats]);

  // دوال تحديد الأعضاء
  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
      }
      return newSet;
    });
  };

  const selectAllMembers = () => {
    if (selectedMembers.size === members.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(members.map(m => m.id)));
    }
  };

  const clearSelection = () => {
    setSelectedMembers(new Set());
  };

  // تصدير الأعضاء المحددين
  const handleExportSelected = async (exportFormat = 'excel') => {
    if (selectedMembers.size === 0) {
      toast.error(isRTL ? 'يرجى تحديد أعضاء للتصدير' : 'Please select members to export');
      return;
    }
    
    try {
      const token = safeLocalStorage.getItem('auth_token');
      
      // جلب بيانات الأعضاء المحددين
      const selectedData = members.filter(m => selectedMembers.has(m.id));
      
      if (exportFormat === 'excel') {
        // تصدير Excel عبر POST للأعضاء المحددين
        const response = await axios.post(`${API}/export/members/selected`, {
          member_ids: Array.from(selectedMembers),
          format: 'excel'
        }, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `selected-members-${selectedMembers.size}-${new Date().toISOString().split('T')[0]}.xlsx`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        // تصدير CSV عبر POST للأعضاء المحددين
        const response = await axios.post(`${API}/export/members/selected`, {
          member_ids: Array.from(selectedMembers),
          format: 'csv'
        }, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `selected-members-${selectedMembers.size}-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      }
      
      toast.success(isRTL 
        ? `تم تصدير ${selectedMembers.size} أعضاء بنجاح` 
        : `Successfully exported ${selectedMembers.size} members`
      );
      clearSelection();
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  // حذف الأعضاء المحددين
  const handleDeleteSelected = async () => {
    if (selectedMembers.size === 0) {
      toast.error(isRTL ? 'يرجى تحديد أعضاء للحذف' : 'Please select members to delete');
      return;
    }
    
    try {
      setDeleteLoading(true);
      const token = safeLocalStorage.getItem('auth_token');
      
      const response = await axios.post(`${API}/members/delete-selected`, {
        member_ids: Array.from(selectedMembers)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isRTL 
        ? `تم حذف ${response.data.deleted_count} أعضاء بنجاح` 
        : `Successfully deleted ${response.data.deleted_count} members`
      );
      setCanUndo(true);
      clearSelection();
      setShowDeleteSelectedDialog(false);
      fetchMembers(searchTerm);
    } catch (error) {
      handleApiError(error, navigate, t);
    } finally {
      setDeleteLoading(false);
    }
  };

  // إنشاء ملف قالب للأعضاء
  const handleCreateFile = () => {
    const template = {
      members: [
        { 
          telegram_id: "123456789", 
          first_name: "اسم العضو", 
          last_name: "اسم العائلة",
          username: "username",
          interests: ["اهتمام1", "اهتمام2"],
          notes: "ملاحظات"
        }
      ]
    };
    const dataStr = JSON.stringify(template, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "members-template.json";
    link.click();
    URL.revokeObjectURL(url);
    toast.success(t('members.templateCreated'));
  };

  // تصدير الأعضاء (Excel/CSV)
  const handleExportFile = async (exportFormat = 'excel') => {
    try {
      const token = safeLocalStorage.getItem('auth_token');
      
      // Build query params with current filters
      const params = new URLSearchParams();
      params.append('format', exportFormat);
      if (searchTerm) params.append('search', searchTerm);
      if (selectedGroup) params.append('group_id', selectedGroup);
      if (selectedActivityLevel) params.append('activity_level', selectedActivityLevel);
      if (selectedQuality) params.append('quality', selectedQuality);
      if (selectedInterest) params.append('interest', selectedInterest);
      
      // Fetch export file from backend
      const response = await axios.get(`${API}/export/members?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: exportFormat === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          : 'text/csv' 
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `members-export-${new Date().toISOString().split('T')[0]}.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success(isRTL ? 'تم تصدير الأعضاء بنجاح' : 'Members exported successfully');
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  // استيراد الأعضاء
  const handleImportFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.csv';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        try {
          const text = await file.text();
          const data = JSON.parse(text);
          const membersCount = data.members?.length || data.length || 0;
          toast.success(t('members.importSuccess', { count: membersCount }));
          fetchMembers(); // إعادة تحميل القائمة
        } catch (error) {
          toast.error(t('members.importError'));
        }
      }
    };
    input.click();
  };

  // حذف جميع الأعضاء
  const handleDeleteAllMembers = async () => {
    try {
      setDeleteLoading(true);
      const token = safeLocalStorage.getItem('auth_token');
      const params = selectedGroup ? `?group_id=${selectedGroup}` : '';
      const response = await axios.delete(`${API}/members${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('members.deleteSuccess', { count: response.data.deleted_count || totalCount }));
      setCanUndo(true);
      setShowDeleteDialog(false);
      fetchMembers();
    } catch (error) {
      handleApiError(error, navigate, t);
    } finally {
      setDeleteLoading(false);
    }
  };

  // حذف عضو واحد
  const handleDeleteMember = async (memberId) => {
    try {
      setDeleteLoading(true);
      const token = safeLocalStorage.getItem('auth_token');
      await axios.delete(`${API}/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isRTL ? 'تم حذف العضو بنجاح' : 'Member deleted successfully');
      setSelectedMember(null);
      fetchMembers(searchTerm);
    } catch (error) {
      handleApiError(error, navigate, t);
    } finally {
      setDeleteLoading(false);
    }
  };

  // استعادة الأعضاء المحذوفين (تراجع)
  const handleRestoreMembers = async () => {
    try {
      setDeleteLoading(true);
      const token = safeLocalStorage.getItem('auth_token');
      const response = await axios.post(`${API}/members/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('members.restoreSuccess', { count: response.data.restored_count }));
      setCanUndo(false);
      fetchMembers();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(t('members.noMembersToRestore'));
      } else {
        handleApiError(error, navigate, t);
      }
      setCanUndo(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div data-testid="members-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-wide">{t('members.title')}</h1>
          <p className="text-neutral-500 mt-1">
            {t('members.totalMembers', { count: totalCount })}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button 
            onClick={handleCreateFile}
            className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-bold shadow-lg shadow-blue-500/30"
            data-testid="create-member-file-btn"
          >
            <Plus size={18} className="ml-2" />
            {t('common.createFile')}
          </Button>
          
          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold shadow-lg shadow-emerald-500/30"
                data-testid="export-member-file-btn"
                disabled={totalCount === 0}
              >
                <Download size={18} className="ml-2" />
                {t('common.export')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="glass border-white/10">
              <DropdownMenuItem 
                onClick={() => handleExportFile('excel')}
                className="cursor-pointer hover:bg-white/10"
              >
                <FileSpreadsheet size={16} className="ml-2 text-green-400" />
                {isRTL ? 'تصدير Excel' : 'Export Excel'}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleExportFile('csv')}
                className="cursor-pointer hover:bg-white/10"
              >
                <FileText size={16} className="ml-2 text-blue-400" />
                {isRTL ? 'تصدير CSV' : 'Export CSV'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button 
            onClick={handleImportFile}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold shadow-lg shadow-purple-500/30"
            data-testid="import-member-file-btn"
          >
            <Upload size={18} className="ml-2" />
            {t('common.import')}
          </Button>
          
          {/* Delete All Button */}
          <Button 
            onClick={() => setShowDeleteDialog(true)}
            className="bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white font-bold shadow-lg shadow-red-500/30"
            data-testid="delete-all-members-btn"
            disabled={totalCount === 0}
          >
            <Trash2 size={18} className="ml-2" />
            {t('members.deleteAll')}
          </Button>
          
          {/* Undo/Restore Button - Always visible */}
          <Button 
            onClick={handleRestoreMembers}
            className={`font-bold shadow-lg ${
              canUndo 
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-amber-500/30' 
                : 'bg-neutral-700 text-neutral-400 cursor-not-allowed shadow-none'
            }`}
            data-testid="restore-members-btn"
            disabled={deleteLoading || !canUndo}
          >
            <RotateCcw size={18} className="ml-2" />
            {isRTL ? 'تراجع' : 'Undo'}
          </Button>
        </div>
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <Zap className="text-green-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-400">{analytics.by_activity?.very_active || 0}</p>
                <p className="text-xs text-neutral-500">{isRTL ? 'نشط جداً' : 'Very Active'}</p>
              </div>
            </div>
          </div>
          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <TrendingUp className="text-blue-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-400">{analytics.by_activity?.active || 0}</p>
                <p className="text-xs text-neutral-500">{isRTL ? 'نشط' : 'Active'}</p>
              </div>
            </div>
          </div>
          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/20">
                <Award className="text-yellow-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-400">{analytics.by_quality?.excellent || 0}</p>
                <p className="text-xs text-neutral-500">{isRTL ? 'جودة ممتازة' : 'Excellent Quality'}</p>
              </div>
            </div>
          </div>
          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/20">
                <Target className="text-cyan-400" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-cyan-400">{analytics.top_interests?.[0]?.count || 0}</p>
                <p className="text-xs text-neutral-500">{analytics.top_interests?.[0]?.name || (isRTL ? 'أعلى اهتمام' : 'Top Interest')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity & Quality Legend - Educational Guide (Collapsible) */}
      <div className="glass rounded-xl mb-6 overflow-hidden">
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="guide" className="border-none">
            <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-white/5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                  <Activity size={18} className="text-white" />
                </div>
                <span className="text-lg font-bold text-cyan-400">
                  {isRTL ? '📊 دليل تصنيف الأعضاء' : '📊 Member Classification Guide'}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <Accordion type="multiple" className="w-full space-y-2">
                {/* Activity Level */}
                <AccordionItem value="activity" className="border border-white/10 rounded-lg overflow-hidden">
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-white/5 bg-black/20">
                    <div className="flex items-center gap-2">
                      <Activity size={16} className="text-blue-400" />
                      <span className="font-semibold text-sm">{isRTL ? 'مستوى النشاط' : 'Activity Level'}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3 pt-2 bg-black/10">
                    <p className="text-xs text-neutral-400 mb-3">
                      {isRTL 
                        ? 'يُقاس بعدد الرسائل التي أرسلها العضو في المجموعة' 
                        : 'Measured by the number of messages sent in the group'}
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 text-xs">
                        <span className="w-3 h-3 rounded-full bg-green-500 shadow-lg shadow-green-500/30"></span>
                        <span className="text-green-400 font-medium w-24">{isRTL ? 'نشط جداً' : 'Very Active'}</span>
                        <span className="text-neutral-400">50+ {isRTL ? 'رسالة' : 'messages'}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="w-3 h-3 rounded-full bg-blue-500 shadow-lg shadow-blue-500/30"></span>
                        <span className="text-blue-400 font-medium w-24">{isRTL ? 'نشط' : 'Active'}</span>
                        <span className="text-neutral-400">20-49 {isRTL ? 'رسالة' : 'messages'}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="w-3 h-3 rounded-full bg-yellow-500 shadow-lg shadow-yellow-500/30"></span>
                        <span className="text-yellow-400 font-medium w-24">{isRTL ? 'متوسط' : 'Moderate'}</span>
                        <span className="text-neutral-400">5-19 {isRTL ? 'رسالة' : 'messages'}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="w-3 h-3 rounded-full bg-gray-500"></span>
                        <span className="text-gray-400 font-medium w-24">{isRTL ? 'منخفض' : 'Low'}</span>
                        <span className="text-neutral-400">&lt;5 {isRTL ? 'رسائل' : 'messages'}</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Quality Score */}
                <AccordionItem value="quality" className="border border-white/10 rounded-lg overflow-hidden">
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-white/5 bg-black/20">
                    <div className="flex items-center gap-2">
                      <Star size={16} className="text-yellow-400" />
                      <span className="font-semibold text-sm">{isRTL ? 'نقاط الجودة' : 'Quality Score'}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3 pt-2 bg-black/10">
                    <p className="text-xs text-neutral-400 mb-3">
                      {isRTL 
                        ? 'تقييم شامل للعضو يساعدك في اختيار الأفضل للتسويق' 
                        : 'Overall member rating to help you choose the best for marketing'}
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 text-xs">
                        <span className="w-3 h-3 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 shadow-lg shadow-yellow-500/30"></span>
                        <span className="text-yellow-400 font-medium w-24">{isRTL ? 'ممتاز' : 'Excellent'}</span>
                        <span className="text-neutral-400">80-100 {isRTL ? 'نقطة' : 'points'}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="w-3 h-3 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 shadow-lg shadow-green-500/30"></span>
                        <span className="text-green-400 font-medium w-24">{isRTL ? 'جيد' : 'Good'}</span>
                        <span className="text-neutral-400">60-79 {isRTL ? 'نقطة' : 'points'}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-400 to-cyan-500 shadow-lg shadow-blue-500/30"></span>
                        <span className="text-blue-400 font-medium w-24">{isRTL ? 'متوسط' : 'Average'}</span>
                        <span className="text-neutral-400">40-59 {isRTL ? 'نقطة' : 'points'}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="w-3 h-3 rounded-full bg-gray-500"></span>
                        <span className="text-gray-400 font-medium w-24">{isRTL ? 'منخفض' : 'Low'}</span>
                        <span className="text-neutral-400">0-39 {isRTL ? 'نقطة' : 'points'}</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* How Quality is Calculated */}
                <AccordionItem value="calculation" className="border border-white/10 rounded-lg overflow-hidden">
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-white/5 bg-black/20">
                    <div className="flex items-center gap-2">
                      <Target size={16} className="text-cyan-400" />
                      <span className="font-semibold text-sm">{isRTL ? 'كيف تُحسب الجودة' : 'How Quality is Calculated'}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-3 pt-2 bg-black/10">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs p-2 rounded bg-black/20">
                        <span className="text-neutral-300 flex items-center gap-2">
                          <Activity size={12} className="text-blue-400" />
                          {isRTL ? 'النشاط (عدد الرسائل)' : 'Activity (Messages)'}
                        </span>
                        <span className="text-cyan-400 font-mono font-bold">40 pts</span>
                      </div>
                      <div className="flex items-center justify-between text-xs p-2 rounded bg-black/20">
                        <span className="text-neutral-300 flex items-center gap-2">
                          <Target size={12} className="text-purple-400" />
                          {isRTL ? 'الاهتمامات المكتشفة' : 'Discovered Interests'}
                        </span>
                        <span className="text-cyan-400 font-mono font-bold">30 pts</span>
                      </div>
                      <div className="flex items-center justify-between text-xs p-2 rounded bg-black/20">
                        <span className="text-neutral-300 flex items-center gap-2">
                          <User size={12} className="text-green-400" />
                          Username
                        </span>
                        <span className="text-cyan-400 font-mono font-bold">15 pts</span>
                      </div>
                      <div className="flex items-center justify-between text-xs p-2 rounded bg-black/20">
                        <span className="text-neutral-300 flex items-center gap-2">
                          <User size={12} className="text-orange-400" />
                          {isRTL ? 'الاسم الكامل' : 'Full Name'}
                        </span>
                        <span className="text-cyan-400 font-mono font-bold">15 pts</span>
                      </div>
                      <div className="border-t border-white/10 pt-2 mt-2 flex items-center justify-between font-medium text-sm">
                        <span className="text-white">{isRTL ? 'المجموع الكلي' : 'Total Maximum'}</span>
                        <span className="text-cyan-400 font-mono font-bold">100 pts</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Filters */}
      <div className="glass rounded-xl p-4">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="flex flex-col gap-2 flex-1 min-w-[200px]">
            <span className="text-sm font-bold text-white">{isRTL ? 'البحث' : 'Search'}</span>
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500" size={18} />
              <Input
                data-testid="search-members"
                placeholder={t('members.searchMembers')}
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(0);
                }}
                className="pr-10"
              />
            </div>
          </div>

          {/* Activity Level Filter */}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-bold text-white">{isRTL ? 'مستوى النشاط' : 'Activity Level'}</span>
            <Select 
              value={selectedActivityLevel || "all"} 
              onValueChange={(val) => {
                setSelectedActivityLevel(val === "all" ? '' : val);
                setPage(0);
              }}
            >
              <SelectTrigger data-testid="activity-filter" className="w-[180px] bg-purple-600 hover:bg-purple-700 border-purple-500 text-white font-medium">
                <SelectValue placeholder={isRTL ? 'مستوى النشاط' : 'Activity Level'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="flex items-center justify-between w-full gap-2">
                    <span>{isRTL ? 'الكل' : 'All'}</span>
                    <span className="text-xs text-neutral-400">({filterStats?.total || 0})</span>
                  </span>
                </SelectItem>
                <SelectItem value="very_active">
                  <span className="flex items-center justify-between w-full gap-2">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      {isRTL ? 'نشط جداً' : 'Very Active'}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-xs text-white font-medium">({filterStats?.activity_levels?.very_active || 0})</span>
                      <span className="text-neutral-500">/</span>
                      <CircularProgress 
                        percentage={filterStats?.total ? ((filterStats?.activity_levels?.very_active || 0) / filterStats.total) * 100 : 0} 
                        size={16} 
                        color="#22C55E" 
                      />
                      <span className="text-xs text-green-400 font-medium">
                        {filterStats?.total ? Math.round(((filterStats?.activity_levels?.very_active || 0) / filterStats.total) * 100) : 0}%
                      </span>
                    </span>
                  </span>
                </SelectItem>
                <SelectItem value="active">
                  <span className="flex items-center justify-between w-full gap-2">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      {isRTL ? 'نشط' : 'Active'}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-xs text-white font-medium">({filterStats?.activity_levels?.active || 0})</span>
                      <span className="text-neutral-500">/</span>
                      <CircularProgress 
                        percentage={filterStats?.total ? ((filterStats?.activity_levels?.active || 0) / filterStats.total) * 100 : 0} 
                        size={16} 
                        color="#3B82F6" 
                      />
                      <span className="text-xs text-blue-400 font-medium">
                        {filterStats?.total ? Math.round(((filterStats?.activity_levels?.active || 0) / filterStats.total) * 100) : 0}%
                      </span>
                    </span>
                  </span>
                </SelectItem>
                <SelectItem value="moderate">
                  <span className="flex items-center justify-between w-full gap-2">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                      {isRTL ? 'متوسط' : 'Moderate'}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-xs text-white font-medium">({filterStats?.activity_levels?.moderate || 0})</span>
                      <span className="text-neutral-500">/</span>
                      <CircularProgress 
                        percentage={filterStats?.total ? ((filterStats?.activity_levels?.moderate || 0) / filterStats.total) * 100 : 0} 
                        size={16} 
                        color="#F59E0B" 
                      />
                      <span className="text-xs text-yellow-400 font-medium">
                        {filterStats?.total ? Math.round(((filterStats?.activity_levels?.moderate || 0) / filterStats.total) * 100) : 0}%
                      </span>
                    </span>
                  </span>
                </SelectItem>
                <SelectItem value="low">
                  <span className="flex items-center justify-between w-full gap-2">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                      {isRTL ? 'منخفض' : 'Low'}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-xs text-white font-medium">({filterStats?.activity_levels?.low || 0})</span>
                      <span className="text-neutral-500">/</span>
                      <CircularProgress 
                        percentage={filterStats?.total ? ((filterStats?.activity_levels?.low || 0) / filterStats.total) * 100 : 0} 
                        size={16} 
                        color="#6B7280" 
                      />
                      <span className="text-xs text-gray-400 font-medium">
                        {filterStats?.total ? Math.round(((filterStats?.activity_levels?.low || 0) / filterStats.total) * 100) : 0}%
                      </span>
                    </span>
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Quality Filter */}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-bold text-white">{isRTL ? 'نقاط الجودة' : 'Quality Score'}</span>
            <Select 
              value={selectedQuality || "all"} 
              onValueChange={(val) => {
                setSelectedQuality(val === "all" ? '' : val);
                setPage(0);
              }}
            >
              <SelectTrigger data-testid="quality-filter" className="w-[180px] bg-purple-600 hover:bg-purple-700 border-purple-500 text-white font-medium">
                <SelectValue placeholder={isRTL ? 'نقاط الجودة' : 'Quality Score'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="flex items-center justify-between w-full gap-2">
                    <span>{isRTL ? 'الكل' : 'All'}</span>
                    <span className="text-xs text-neutral-400">({filterStats?.total || 0})</span>
                  </span>
                </SelectItem>
                <SelectItem value="excellent">
                  <span className="flex items-center justify-between w-full gap-2">
                    <span className="flex items-center gap-2">
                      <Award size={14} className="text-yellow-500" />
                      {isRTL ? 'ممتاز' : 'Excellent'}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-xs text-white font-medium">({filterStats?.quality_scores?.excellent || 0})</span>
                      <span className="text-neutral-500">/</span>
                      <CircularProgress 
                        percentage={filterStats?.total ? ((filterStats?.quality_scores?.excellent || 0) / filterStats.total) * 100 : 0} 
                        size={16} 
                        color="#EAB308" 
                      />
                      <span className="text-xs text-yellow-400 font-medium">
                        {filterStats?.total ? Math.round(((filterStats?.quality_scores?.excellent || 0) / filterStats.total) * 100) : 0}%
                      </span>
                    </span>
                  </span>
                </SelectItem>
                <SelectItem value="good">
                  <span className="flex items-center justify-between w-full gap-2">
                    <span className="flex items-center gap-2">
                      <Star size={14} className="text-green-500" />
                      {isRTL ? 'جيد' : 'Good'}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-xs text-white font-medium">({filterStats?.quality_scores?.good || 0})</span>
                      <span className="text-neutral-500">/</span>
                      <CircularProgress 
                        percentage={filterStats?.total ? ((filterStats?.quality_scores?.good || 0) / filterStats.total) * 100 : 0} 
                        size={16} 
                        color="#22C55E" 
                      />
                      <span className="text-xs text-green-400 font-medium">
                        {filterStats?.total ? Math.round(((filterStats?.quality_scores?.good || 0) / filterStats.total) * 100) : 0}%
                      </span>
                    </span>
                  </span>
                </SelectItem>
                <SelectItem value="average">
                  <span className="flex items-center justify-between w-full gap-2">
                    <span className="flex items-center gap-2">
                      <Target size={14} className="text-blue-500" />
                      {isRTL ? 'متوسط' : 'Average'}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-xs text-white font-medium">({filterStats?.quality_scores?.average || 0})</span>
                      <span className="text-neutral-500">/</span>
                      <CircularProgress 
                        percentage={filterStats?.total ? ((filterStats?.quality_scores?.average || 0) / filterStats.total) * 100 : 0} 
                        size={16} 
                        color="#3B82F6" 
                      />
                      <span className="text-xs text-blue-400 font-medium">
                        {filterStats?.total ? Math.round(((filterStats?.quality_scores?.average || 0) / filterStats.total) * 100) : 0}%
                      </span>
                    </span>
                  </span>
                </SelectItem>
                <SelectItem value="low">
                  <span className="flex items-center justify-between w-full gap-2">
                    <span className="flex items-center gap-2">
                      <User size={14} className="text-gray-500" />
                      {isRTL ? 'منخفض' : 'Low'}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-xs text-white font-medium">({filterStats?.quality_scores?.low || 0})</span>
                      <span className="text-neutral-500">/</span>
                      <CircularProgress 
                        percentage={filterStats?.total ? ((filterStats?.quality_scores?.low || 0) / filterStats.total) * 100 : 0} 
                        size={16} 
                        color="#6B7280" 
                      />
                      <span className="text-xs text-gray-400 font-medium">
                        {filterStats?.total ? Math.round(((filterStats?.quality_scores?.low || 0) / filterStats.total) * 100) : 0}%
                      </span>
                    </span>
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Interest Filter */}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-bold text-white">{isRTL ? 'الاهتمامات' : 'Interests'}</span>
            <Select 
              value={selectedInterest || "all"} 
              onValueChange={(val) => {
                setSelectedInterest(val === "all" ? '' : val);
                setPage(0);
              }}
            >
              <SelectTrigger data-testid="interest-filter" className="w-[200px] bg-purple-600 hover:bg-purple-700 border-purple-500 text-white font-medium">
                <SelectValue placeholder={isRTL ? 'الاهتمامات' : 'Interests'} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all">
                  <span className="flex items-center justify-between w-full gap-2">
                    <span>{isRTL ? 'الكل' : 'All'}</span>
                    <span className="text-xs text-neutral-400">({filterStats?.total || 0})</span>
                  </span>
                </SelectItem>
                {/* Show interests from filter stats if available */}
                {filterStats?.interests?.length > 0 ? (
                  filterStats.interests.map((interest, idx) => (
                    <SelectItem key={idx} value={interest.name}>
                      <span className="flex items-center justify-between w-full gap-2">
                        <span className="flex items-center gap-2">
                          <span 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: INTEREST_COLORS[interest.name] || LEGACY_INTEREST_COLORS[interest.name] || '#808080' }}
                          ></span>
                          {translateInterest(interest.name, isRTL)}
                        </span>
                        <span className="text-xs text-neutral-400">({interest.count})</span>
                      </span>
                    </SelectItem>
                  ))
                ) : (
                  TELEGRAM_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color }}
                        ></div>
                        <span>{isRTL ? cat.name_ar : cat.name_en}</span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Group Filter */}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-bold text-white">{isRTL ? 'المجموعة' : 'Group'}</span>
            <Select 
              value={selectedGroup || "all"} 
              onValueChange={(val) => {
                setSelectedGroup(val === "all" ? '' : val);
                setPage(0);
              }}
            >
              <SelectTrigger data-testid="group-filter" className="w-[220px] bg-purple-600 hover:bg-purple-700 border-purple-500 text-white font-medium">
                <SelectValue placeholder={isRTL ? 'المجموعة' : 'Group'} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all">
                  <div className="flex items-center justify-between gap-3 w-full">
                    <span>{t('common.all')}</span>
                    <span className="bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full text-xs font-mono font-bold">
                      {totalCount}
                    </span>
                  </div>
                </SelectItem>
                {groups.map(g => (
                  <SelectItem key={g.id} value={g.id}>
                    <div className="flex items-center justify-between gap-3 w-full">
                      <span className="truncate max-w-[120px]" title={g.group_name}>{g.group_name}</span>
                      <span className="bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full text-xs font-mono font-bold flex-shrink-0">
                        {g.members_count || 0}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Clear Filters */}
          {(searchTerm || selectedCategory || selectedGroup || selectedActivityLevel || selectedQuality || selectedInterest) && (
            <Button 
              data-testid="clear-filters"
              variant="ghost" 
              size="sm"
              onClick={clearFilters}
            >
              <X size={16} className="ml-1" />
              {t('common.clearFilters')}
            </Button>
          )}
        </div>
      </div>

      {/* Selection Actions Bar */}
      {selectedMembers.size > 0 && (
        <div className="glass rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-cyan-400">
              <CheckSquare size={20} />
              <span className="font-bold">{selectedMembers.size}</span>
              <span className="text-neutral-400">{isRTL ? 'عضو محدد' : 'members selected'}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={clearSelection}
              className="text-neutral-400 hover:text-white"
            >
              <X size={16} className="ml-1" />
              {isRTL ? 'إلغاء التحديد' : 'Clear selection'}
            </Button>
          </div>
          <div className="flex gap-2">
            {/* زر حذف المحدد */}
            <Button 
              onClick={() => setShowDeleteSelectedDialog(true)}
              className="bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white font-bold shadow-lg shadow-red-500/30"
              data-testid="delete-selected-btn"
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <span className="spinner-sm ml-2"></span>
              ) : (
                <Trash2 size={18} className="ml-2" />
              )}
              {isRTL ? `حذف المحدد (${selectedMembers.size})` : `Delete Selected (${selectedMembers.size})`}
            </Button>
            
            {/* زر تصدير المحدد */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-bold shadow-lg shadow-cyan-500/30"
                  data-testid="export-selected-btn"
                >
                  <Download size={18} className="ml-2" />
                  {isRTL ? `تصدير المحدد (${selectedMembers.size})` : `Export Selected (${selectedMembers.size})`}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass border-white/10">
                <DropdownMenuItem 
                  onClick={() => handleExportSelected('excel')}
                  className="cursor-pointer hover:bg-white/10"
                >
                  <FileSpreadsheet size={16} className="ml-2 text-green-400" />
                  {isRTL ? 'تصدير Excel' : 'Export Excel'}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleExportSelected('csv')}
                  className="cursor-pointer hover:bg-white/10"
                >
                  <FileText size={16} className="ml-2 text-blue-400" />
                  {isRTL ? 'تصدير CSV' : 'Export CSV'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      {/* Members Table */}
      <div className="glass rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="overflow-x-auto p-4">
            {/* Table Header with White Rounded Border */}
            <div className="members-table-header-wrapper">
              <table className="data-table">
                <thead>
                  <tr style={{ height: '70px' }}>
                  <th style={{ 
                    width: '50px',
                    textAlign: 'center', 
                    padding: '1.5rem 0.5rem',
                    verticalAlign: 'middle'
                  }}>
                    <div 
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer mx-auto transition-all ${
                        selectedMembers.size === members.length && members.length > 0
                          ? 'bg-cyan-500 border-cyan-500' 
                          : selectedMembers.size > 0 
                            ? 'bg-cyan-500/50 border-cyan-500'
                            : 'border-neutral-500 hover:border-cyan-400'
                      }`}
                      onClick={selectAllMembers}
                      data-testid="select-all-members"
                    >
                      {(selectedMembers.size === members.length && members.length > 0) && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {(selectedMembers.size > 0 && selectedMembers.size < members.length) && (
                        <div className="w-2 h-2 bg-white rounded-sm"></div>
                      )}
                    </div>
                  </th>
                  <th style={{ 
                    width: '20%',
                    color: '#FFFFFF', 
                    fontSize: '1.3rem', 
                    fontWeight: '700', 
                    textAlign: 'center', 
                    padding: '1.5rem 1rem',
                    verticalAlign: 'middle'
                  }}>
                    {t('members.memberName')}
                  </th>
                  <th style={{ 
                    width: '12%',
                    color: '#FFFFFF', 
                    fontSize: '1.3rem', 
                    fontWeight: '700', 
                    textAlign: 'center', 
                    padding: '1.5rem 1rem',
                    verticalAlign: 'middle'
                  }}>
                    {isRTL ? 'النشاط' : 'Activity'}
                  </th>
                  <th style={{ 
                    width: '12%',
                    color: '#FFFFFF', 
                    fontSize: '1.3rem', 
                    fontWeight: '700', 
                    textAlign: 'center', 
                    padding: '1.5rem 1rem',
                    verticalAlign: 'middle'
                  }}>
                    {isRTL ? 'الجودة' : 'Quality'}
                  </th>
                  <th style={{ 
                    width: '20%',
                    color: '#FFFFFF', 
                    fontSize: '1.3rem', 
                    fontWeight: '700', 
                    textAlign: 'center', 
                    padding: '1.5rem 1rem',
                    verticalAlign: 'middle'
                  }}>
                    {t('members.interests')}
                  </th>
                  <th style={{ 
                    width: '10%',
                    color: '#FFFFFF', 
                    fontSize: '1.3rem', 
                    fontWeight: '700', 
                    textAlign: 'center', 
                    padding: '1.5rem 1rem',
                    verticalAlign: 'middle'
                  }}>
                    {isRTL ? 'الرسائل' : 'Messages'}
                  </th>
                  <th style={{ 
                    width: '14%',
                    color: '#FFFFFF', 
                    fontSize: '1.3rem', 
                    fontWeight: '700', 
                    textAlign: 'center', 
                    padding: '1.5rem 1rem',
                    verticalAlign: 'middle'
                  }}>
                    {t('members.dateAdded')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {members.map(member => (
                  <MemberRow 
                    key={member.id} 
                    member={member}
                    onSelect={setSelectedMember}
                    onToggleSelect={toggleMemberSelection}
                    isSelected={selectedMembers.has(member.id)}
                    t={t}
                    isRTL={isRTL}
                  />
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
        
        {/* Empty State - Show inside table area when no members */}
        {!loading && members.length === 0 && (
          <div className="empty-state">
            <User className="empty-state-icon mx-auto text-neutral-600" />
            <h3 className="text-lg font-semibold text-neutral-400">{t('members.noMembers')}</h3>
            <p className="text-neutral-500 mt-2">{t('members.membersWillAppear')}</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-500">
            {t('members.showingRange', { start: page * limit + 1, end: Math.min((page + 1) * limit, totalCount), total: totalCount })}
          </p>
          <div className="flex gap-2">
            <Button
              data-testid="prev-page"
              variant="ghost"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronRight size={18} />
              {t('common.previous')}
            </Button>
            <Button
              data-testid="next-page"
              variant="ghost"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
            >
              {t('common.next')}
              <ChevronLeft size={18} />
            </Button>
          </div>
        </div>
      )}

      {/* Member Detail Modal */}
      <MemberDetail 
        member={selectedMember} 
        onClose={() => setSelectedMember(null)}
        onDelete={handleDeleteMember}
        t={t}
        isRTL={isRTL}
        deleteLoading={deleteLoading}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="glass border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">
              {t('members.confirmDelete')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedGroup 
                ? t('members.deleteGroupMembersWarning')
                : t('members.deleteAllMembersWarning', { count: totalCount })
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteAllMembers}
              className="bg-red-500 hover:bg-red-600"
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <span className="spinner-sm ml-2"></span>
              ) : (
                <Trash2 size={16} className="ml-2" />
              )}
              {t('members.deleteConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Selected Members Confirmation Dialog */}
      <AlertDialog open={showDeleteSelectedDialog} onOpenChange={setShowDeleteSelectedDialog}>
        <AlertDialogContent className="glass border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">
              {isRTL ? 'تأكيد حذف الأعضاء المحددين' : 'Confirm Delete Selected Members'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isRTL 
                ? `هل أنت متأكد من حذف ${selectedMembers.size} أعضاء محددين؟ سيتم حفظهم مؤقتاً ويمكنك استعادتهم.`
                : `Are you sure you want to delete ${selectedMembers.size} selected members? They will be saved temporarily and can be restored.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSelected}
              className="bg-red-500 hover:bg-red-600"
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <span className="spinner-sm ml-2"></span>
              ) : (
                <Trash2 size={16} className="ml-2" />
              )}
              {isRTL ? `حذف ${selectedMembers.size} أعضاء` : `Delete ${selectedMembers.size} members`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
